import { Component, inject, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificacaoBarService } from '../../services/notifcacaoBar.service';
import { TranslateModule } from '@ngx-translate/core';
import { NotificacaoUsuarioSistemaService } from '../../services/notificacaoUsuario.service';
import { NovidadeSistema } from '../../model/NovidadeSistema.model';
import { NotificacaoUsuarioSistema } from '../../model/NotificacaoUsuarioSistema.model';
import { trigger, transition, style, animate } from '@angular/animations';
import { Router } from '@angular/router';
import { UsuarioExportacaoService } from '../../../Consultas/components/exportacoes/services/ExportacaoUsuario.service';
import { load } from '@amcharts/amcharts5/.internal/core/util/Net';
import { paraUtc } from '../../../../shared/utils/functions/paraUtc';

interface NotificacaoUnificada {
  id: number;
  titulo: string;
  descricao: string;
  imagem?: string;
  link?: string;
  dataCadastro: Date;
  tipo: 'novidade' | 'notificacao';
  novidadeId?: number;
  notificacaoId?: number;
  usuarioExportacaoId?: number;
}

@Component({
  selector: 'app-notificacao-bar',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './notificacao-bar.component.html',
  styleUrls: ['./notificacao-bar.component.scss'],
  animations: [
    trigger('cardAnimation', [
      transition(':leave', [
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({
          opacity: 0,
          transform: 'translateX(100px) scale(0.8)',
          height: '0px',
          marginBottom: '0px',
          paddingTop: '0px',
          paddingBottom: '0px'
        }))
      ])
    ])
  ]
})
export class NotificacaoBarComponent implements OnInit {


  private readonly notificacaoBarService = inject(NotificacaoBarService);
  private readonly _notificacaoUsuario = inject(NotificacaoUsuarioSistemaService)
  private readonly _notificacaoBarService = inject(NotificacaoBarService);
  private readonly _usuarioExportacaoService = inject(UsuarioExportacaoService);
  private readonly _notificacaoUsuarioService = inject(NotificacaoUsuarioSistemaService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly _router = inject(Router);


  listaDeNovidadesDoSistema: NovidadeSistema[] = [];
  listaDeNotificacoesDoUsuario: NotificacaoUsuarioSistema[] = [];
  listaUnificada: NotificacaoUnificada[] = [];

  ngOnInit() {
    this.notificacaoBarService.novidadesDoSistema$.subscribe(novidadesSistema => {
      this.listaDeNovidadesDoSistema = novidadesSistema;
      this.criarListaUnificada();
    });
    this.notificacaoBarService.notificacoes$.subscribe(notificacoes => {
      this.listaDeNotificacoesDoUsuario = notificacoes.filter(n => !n.flagVisualizado);
      this.criarListaUnificada();
    });
  }

  criarListaUnificada() {
    const notificacoesBaseadas: NotificacaoUnificada[] = this.listaDeNovidadesDoSistema.map(novidade => ({
      id: novidade.id,
      titulo: this.normalizeMessage(novidade.titulo),
      descricao: this.normalizeMessage(novidade.descricao),
      imagem: novidade.imagem,
      dataCadastro: paraUtc(novidade.dataCadastro),
      tipo: 'novidade' as const,
      novidadeId: novidade.id
    }));

    const notificacoesDiretas: NotificacaoUnificada[] = this.listaDeNotificacoesDoUsuario
      .filter(n => !n.novidadeSistemaId)
      .map(notif => ({
        id: notif.id,
        titulo: this.normalizeMessage(notif.titulo) || 'Notificação',
        descricao: this.normalizeMessage(notif.descricao),
        link: notif.linkDownload,
        dataCadastro: paraUtc(notif.dataCadastro),
        tipo: 'notificacao' as const,
        notificacaoId: notif.id,
        usuarioExportacaoId: notif.usuarioExportacaoId
      }));

    this.listaUnificada = [...notificacoesBaseadas, ...notificacoesDiretas]
      .sort((a, b) => new Date(b.dataCadastro).getTime() - new Date(a.dataCadastro).getTime());

    this.cdr.detectChanges();
  }

  fechar() {
    this.notificacaoBarService.fechar();
  }

  verArtigo(novidade: NovidadeSistema) {
    window.location.href = `/novidade/${novidade.id}`;
  }

  abrirLink(notificacao: NotificacaoUnificada) {
    this.fechar();
    if (notificacao.tipo === 'novidade' && notificacao.novidadeId) {
      window.location.href = `/novidade/${notificacao.novidadeId}`;
    } else if (notificacao.tipo === 'notificacao' && notificacao.link) {
      window.open(notificacao.link, '_blank');
    }

    this._usuarioExportacaoService.getById(notificacao.usuarioExportacaoId).subscribe({
      next: (exportacao) => {
        exportacao.arquivoAcessado = true;
        this._usuarioExportacaoService.save(exportacao).subscribe(() => {
          this._notificacaoUsuarioService
            .deletePeloUsuarioExportacaoId(exportacao.id)
            .subscribe(() => {
              this.cdr.detectChanges();
              this.notificacaoBarService.notificarMudanca();
            });
        });
      }
    });

  }

  irParaExportacoes() {
    this.fechar();
    this._router.navigate(['/exportacoes']);
  }

  marcarComoLido(notificacaoUnificada: NotificacaoUnificada) {
    let notificacao: NotificacaoUsuarioSistema | undefined;

    if (notificacaoUnificada.tipo === 'novidade') {
      notificacao = this.listaDeNotificacoesDoUsuario.find(n => n.novidadeSistemaId === notificacaoUnificada.novidadeId);
    } else {
      notificacao = this.listaDeNotificacoesDoUsuario.find(n => n.id === notificacaoUnificada.notificacaoId);
    }

    if (notificacao) {
      notificacao.flagVisualizado = true;
      this._notificacaoUsuario.save(notificacao).subscribe({
        next: (response) => {
          // Remove da lista local com animação
          setTimeout(() => {
            this.listaUnificada = this.listaUnificada.filter(n => n.id !== notificacaoUnificada.id || n.tipo !== notificacaoUnificada.tipo);

            // Atualiza listas originais
            if (notificacaoUnificada.tipo === 'novidade') {
              this.listaDeNovidadesDoSistema = this.listaDeNovidadesDoSistema.filter(n => n.id !== notificacaoUnificada.novidadeId);
            } else {
              this.listaDeNotificacoesDoUsuario = this.listaDeNotificacoesDoUsuario.filter(n => n.id !== notificacaoUnificada.notificacaoId);
            }

            // Notifica o serviço que houve mudança
            this.notificacaoBarService.notificarMudanca();

            this.cdr.detectChanges();
          }, 50);

          // Atualiza a lista de notificações
          this._notificacaoUsuario.GetNotificacaoUsuario().subscribe(notificacoes => {
            this.listaDeNotificacoesDoUsuario = notificacoes.filter((n: NotificacaoUsuarioSistema) => !n.flagVisualizado);
            this._notificacaoUsuario.getNotificacoes();
          });
        },
        error: (err) => {
          console.error('Erro ao marcar notificação como lida:', err);
        }
      });
    } else {
      console.warn('Notificação não encontrada.');
    }
  }

  atualizarNotificacao() {
    this._notificacaoBarService.notificacoes$.subscribe(notificacoes => {
      this.listaDeNotificacoesDoUsuario = notificacoes;
    });
  }

  private normalizeMessage(value?: string | null): string {
    return (value ?? '').trim();
  }
} 
